import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import AdvertPlayer from '@/components/advert/advert-player'
import { ADVERT_DURATION } from '@/constants/advert'

const ADVERT_SECONDS = Math.round(ADVERT_DURATION / 1000)

const HeroSection: FC = (): ReactNode => {
    return (
        <section id='advert' aria-labelledby='hero-title' className='relative scroll-mt-16 overflow-hidden bg-ink text-paper'>
            <div
                aria-hidden='true'
                className='pointer-events-none absolute inset-x-0 top-0 h-[36rem] bg-[radial-gradient(60%_60%_at_50%_0%,color-mix(in_oklab,var(--volt)_14%,transparent),transparent)]'
            />

            <div className='relative mx-auto max-w-7xl px-4 pb-20 pt-8 sm:px-6 sm:pt-10 lg:px-8 lg:pb-28'>
                <div className='mb-4 flex items-center justify-between gap-4 sm:mb-5'>
                    <p className='flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.18em] text-paper/65'>
                        <span className='relative flex size-2'>
                            <span className='absolute inline-flex size-full animate-ping rounded-full bg-volt opacity-75 motion-reduce:hidden' />
                            <span className='relative inline-flex size-2 rounded-full bg-volt' />
                        </span>
                        Now showing
                    </p>
                    <p className='hidden text-xs text-paper/50 sm:block'>
                        Blitz Tech Services · {ADVERT_SECONDS}-second advert
                    </p>
                </div>

                <AdvertPlayer />

                <div className='mt-14 grid gap-8 lg:mt-20 lg:grid-cols-[1.2fr_1fr] lg:items-end lg:gap-16'>
                    <h1
                        id='hero-title'
                        className='text-balance font-heading text-4xl font-bold leading-[1.05] tracking-[-0.03em] sm:text-5xl lg:text-6xl'
                    >
                        Brilliant websites. <span className='text-volt'>Down-to-earth prices.</span>
                    </h1>

                    <div>
                        <p className='text-lg leading-relaxed text-paper/75'>
                            Blitz Tech Services designs and builds websites for local businesses and big companies, at prices that make sense for your budget.
                        </p>

                        <div className='mt-8 flex flex-wrap gap-3'>
                            <Link href='#contact' className={cn(buttonVariants({ size: 'xl' }), 'focus-visible:ring-volt/60')}>
                                Get a quote
                                <ArrowRight className='size-4' aria-hidden='true' />
                            </Link>
                            <Link
                                href='#services'
                                className='inline-flex h-12 items-center gap-2 rounded-full border border-paper/20 px-6 text-base font-semibold text-paper transition-colors hover:bg-paper/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-volt'
                            >
                                See what we do
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default HeroSection
